import {
    Anchor,
    Badge,
    Button,
    Drawer,
    Group,
    Image,
    Loader,
    NumberInput,
    Select,
    Stack,
    Text,
    TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AdminPortalPartnerRO } from '@routellers/types/dist/partners-portal/admin/admin-portal-partner.ro';
import { PortalPartnerStatusEnum } from '@routellers/types/dist/partners-portal/enum/portal-partner-status.enum';
import { PortalPartnerTypeEnum } from '@routellers/types/dist/partners-portal/enum/portal-partner-type.enum';
import { ImageTypeEnum } from '@routellers/types/dist/upload/enum/image-type.enum';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../api';
import { PhotoUpload } from '../components/editor';
import { formatDate, formatMoneyMap } from '../utils/portal';
import {
    portalPartnerStatusColors,
    portalPartnerStatusLabel,
    portalPartnerStatusOptions,
} from '../utils/portal-partner-status';

type Props = {
    opened: boolean;
    onClose: () => void;
    partner: AdminPortalPartnerRO | null;
    onSaved: () => void;
    onDeleted?: () => void;
};

export const PartnerEditor = ({ opened, onClose, partner, onSaved, onDeleted }: Props) => {
    const { t } = useTranslation();
    const statusOptions = useMemo(() => portalPartnerStatusOptions(t), [t]);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [statsLoading, setStatsLoading] = useState(false);
    const [qrLoading, setQrLoading] = useState(false);
    const [commissionPercent, setCommissionPercent] = useState<number | ''>('');
    const [discountPercent, setDiscountPercent] = useState<number | ''>('');
    const [promocode, setPromocode] = useState('');
    const [status, setStatus] = useState<string | null>(null);
    const [name, setName] = useState('');
    const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
    const [companyName, setCompanyName] = useState('');
    const [statsText, setStatsText] = useState<string | null>(null);
    const [inviteUrl, setInviteUrl] = useState<string | null>(null);
    const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
    const [promoUrl, setPromoUrl] = useState<string | null>(null);
    const [promoQrDataUrl, setPromoQrDataUrl] = useState<string | null>(null);

    const isVenue = partner?.type === PortalPartnerTypeEnum.VENUE;
    const isAuthor = partner?.type === PortalPartnerTypeEnum.AUTHOR;

    useEffect(() => {
        if (!opened || !partner) return;

        setCommissionPercent(partner.commissionPercent ?? 0);
        setDiscountPercent(partner.discountPercent ?? 20);
        setPromocode(partner.promocode || '');
        setStatus(partner.status || PortalPartnerStatusEnum.REGISTERED);
        setName(partner.name || '');
        setAvatarUrl(partner.avatarUrl || null);
        setCompanyName(partner.companyName || '');
        setStatsText(null);
        setInviteUrl(partner.inviteUrl || null);
        setQrDataUrl(null);
        setPromoUrl(null);
        setPromoQrDataUrl(null);

        let cancelled = false;

        const loadStats = async () => {
            setStatsLoading(true);

            try {
                const { data } = await api.partnersPortalAdminApi.partnerStats(partner.id);

                if (cancelled || !data) return;

                setStatsText(
                    [
                        t('editors.partner.statsGames', { count: data.totalGames ?? 0 }),
                        t('editors.partner.statsSales', {
                            amount: formatMoneyMap(data.totalSales),
                        }),
                        t('editors.partner.statsCommission', {
                            amount: formatMoneyMap(data.totalCommission),
                        }),
                        data.qrScans != null
                            ? t('editors.partner.statsQrScans', { count: data.qrScans })
                            : null,
                        data.claims != null
                            ? t('editors.partner.statsClaims', { count: data.claims })
                            : null,
                        data.claimedGuests != null
                            ? t('editors.partner.statsGuests', { count: data.claimedGuests })
                            : null,
                        data.purchasesCount != null
                            ? t('editors.partner.statsPurchases', {
                                  count: data.purchasesCount,
                              })
                            : null,
                    ]
                        .filter(Boolean)
                        .join(' · '),
                );
            } catch {
                // Stats are optional for editing.
            } finally {
                if (!cancelled) setStatsLoading(false);
            }
        };

        const loadQr = async () => {
            if (!isVenue) return;
            setQrLoading(true);

            try {
                const [invite, promo] = await Promise.allSettled([
                    api.partnersPortalAdminApi.inviteQr(partner.id),
                    api.partnersPortalAdminApi.promoQr(partner.id),
                ]);

                if (cancelled) return;

                if (invite.status === 'fulfilled' && invite.value.data) {
                    setInviteUrl(invite.value.data.inviteUrl);
                    setQrDataUrl(invite.value.data.qrDataUrl);
                }

                if (promo.status === 'fulfilled' && promo.value.data) {
                    setPromoUrl(promo.value.data.promoUrl);
                    setPromoQrDataUrl(promo.value.data.qrDataUrl);
                }
            } finally {
                if (!cancelled) setQrLoading(false);
            }
        };

        loadStats();
        loadQr();

        return () => {
            cancelled = true;
        };
    }, [opened, partner, isVenue, t]);

    const save = async () => {
        if (!partner) return;

        if (
            commissionPercent === '' ||
            Number.isNaN(Number(commissionPercent)) ||
            Number(commissionPercent) < 0 ||
            Number(commissionPercent) > 100
        ) {
            notifications.show({
                color: 'red',
                message: t('editors.partner.commissionRangeError'),
            });

            return;
        }

        if (
            isVenue &&
            (discountPercent === '' ||
                Number.isNaN(Number(discountPercent)) ||
                Number(discountPercent) < 0 ||
                Number(discountPercent) > 100)
        ) {
            notifications.show({
                color: 'red',
                message: t('editors.partner.discountRangeError'),
            });

            return;
        }

        if (!status) {
            notifications.show({ color: 'red', message: t('editors.partner.statusRequired') });

            return;
        }

        setSaving(true);

        try {
            await api.partnersPortalAdminApi.updatePartner({
                id: partner.id,
                data: {
                    commissionPercent: Number(commissionPercent),
                    status: status as PortalPartnerStatusEnum,
                    name: name.trim() || undefined,
                    ...(isAuthor ? { avatarUrl } : {}),
                    ...(isVenue
                        ? {
                              promocode: promocode.trim() || undefined,
                              companyName: companyName.trim() || undefined,
                              discountPercent: Number(discountPercent),
                          }
                        : {}),
                },
            });

            notifications.show({ color: 'green', message: t('editors.partner.saved') });
            onSaved();
            onClose();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Drawer
            opened={opened}
            onClose={onClose}
            title={partner ? partner.companyName || partner.name : t('editors.partner.title')}
            position='right'
            size='md'
            padding='md'
        >
            {!partner ? (
                <Loader />
            ) : (
                <Stack gap='md'>
                    <Group gap='sm'>
                        <Badge
                            color={
                                portalPartnerStatusColors[
                                    partner.status as PortalPartnerStatusEnum
                                ] || 'gray'
                            }
                            variant='light'
                        >
                            {portalPartnerStatusLabel(
                                t,
                                partner.status as PortalPartnerStatusEnum,
                            ) || partner.status}
                        </Badge>
                        <Badge variant='light' color='blue'>
                            {isVenue ? t('editors.partner.venue') : t('editors.partner.author')}
                        </Badge>
                    </Group>

                    <Stack gap={4}>
                        <Text size='sm' c='dimmed'>
                            {partner.email || t('editors.partner.noEmailYet')}
                        </Text>
                        {partner.cityTitle && (
                            <Text size='sm' c='dimmed'>
                                {t('editors.partner.cityLabel', {
                                    city: `${partner.cityTitle}${
                                        partner.cityCountry ? ` (${partner.cityCountry})` : ''
                                    }`,
                                })}
                            </Text>
                        )}
                        {partner.googleAddress && (
                            <Text size='sm' c='dimmed'>
                                {t('editors.partner.addressLabel', {
                                    address: partner.googleAddress,
                                })}
                            </Text>
                        )}
                        {partner.googlePhone && (
                            <Text size='sm' c='dimmed'>
                                {t('editors.partner.phoneLabel', { phone: partner.googlePhone })}
                            </Text>
                        )}
                        {partner.googleWebsite && (
                            <Text size='sm' c='dimmed'>
                                {t('editors.partner.websiteLabel')}{' '}
                                <Anchor href={partner.googleWebsite} target='_blank' size='sm'>
                                    {partner.googleWebsite}
                                </Anchor>
                            </Text>
                        )}
                        {partner.googleRating != null && (
                            <Text size='sm' c='dimmed'>
                                {t('editors.partner.ratingLabel', {
                                    rating: partner.googleRating,
                                })}
                                {partner.googleReviewsCount != null
                                    ? t('editors.partner.reviewsCount', {
                                          count: partner.googleReviewsCount,
                                      })
                                    : ''}
                            </Text>
                        )}
                        <Text size='sm' c='dimmed'>
                            {t('editors.partner.registered', {
                                date: formatDate(partner.createdAt),
                            })}
                        </Text>
                        <Text size='sm' c='dimmed'>
                            {t('editors.partner.emailVerified', {
                                value: partner.emailVerified
                                    ? t('editors.partner.yes')
                                    : t('editors.partner.no'),
                            })}
                        </Text>
                        {statsLoading ? (
                            <Loader size='sm' />
                        ) : (
                            statsText && (
                                <Text size='sm' c='dimmed'>
                                    {statsText}
                                </Text>
                            )
                        )}
                        {isVenue && (
                            <>
                                <Text size='sm' c='dimmed'>
                                    Stripe Connect:{' '}
                                    {partner.stripeConnectCompleted ? 'ready' : 'not connected'}
                                    {partner.stripeConnectAccountId
                                        ? ` (${partner.stripeConnectAccountId})`
                                        : ''}
                                </Text>
                                {(partner.earnedCents != null ||
                                    partner.availableCents != null) && (
                                    <Text size='sm' c='dimmed'>
                                        Ledger: earned{' '}
                                        {formatMoneyMap({ EUR: partner.earnedCents || 0 })} ·
                                        available{' '}
                                        {formatMoneyMap({ EUR: partner.availableCents || 0 })} ·
                                        hold{' '}
                                        {formatMoneyMap({ EUR: partner.pendingHoldCents || 0 })} ·
                                        paid out{' '}
                                        {formatMoneyMap({ EUR: partner.paidOutCents || 0 })}
                                    </Text>
                                )}
                            </>
                        )}
                    </Stack>

                    <TextInput
                        label={t('editors.partner.contactName')}
                        value={name}
                        onChange={(e) => setName(e.currentTarget.value)}
                    />

                    {isAuthor && (
                        <PhotoUpload
                            variant='avatar'
                            label={t('editors.partner.avatar')}
                            description={t('editors.partner.avatarHint')}
                            value={avatarUrl}
                            onChange={setAvatarUrl}
                            imageType={ImageTypeEnum.AVATAR}
                        />
                    )}

                    {isVenue && (
                        <TextInput
                            label={t('editors.partner.companyName')}
                            value={companyName}
                            onChange={(e) => setCompanyName(e.currentTarget.value)}
                        />
                    )}

                    <Select
                        label={t('editors.partner.status')}
                        data={statusOptions}
                        value={status}
                        onChange={setStatus}
                        searchable
                    />

                    <NumberInput
                        label={t('editors.partner.commissionPercent')}
                        min={0}
                        max={100}
                        value={commissionPercent}
                        onChange={(v) => setCommissionPercent(typeof v === 'number' ? v : '')}
                    />

                    {isVenue && (
                        <NumberInput
                            label={t('editors.partner.discountPercent')}
                            description={t('editors.partner.discountPercentHint')}
                            min={0}
                            max={100}
                            value={discountPercent}
                            onChange={(v) => setDiscountPercent(typeof v === 'number' ? v : '')}
                        />
                    )}

                    {isVenue && (
                        <TextInput
                            label={t('editors.partner.promocode')}
                            value={promocode}
                            onChange={(e) => setPromocode(e.currentTarget.value)}
                        />
                    )}

                    {isVenue && (
                        <Stack gap='xs'>
                            <Text size='sm' fw={600}>
                                {t('editors.partner.inviteQr')}
                            </Text>
                            {qrLoading ? (
                                <Loader size='sm' />
                            ) : qrDataUrl ? (
                                <>
                                    <Image src={qrDataUrl} alt='Invite QR' maw={220} />
                                    {inviteUrl && (
                                        <Anchor href={inviteUrl} target='_blank' size='sm'>
                                            {inviteUrl}
                                        </Anchor>
                                    )}
                                </>
                            ) : (
                                <Text size='sm' c='dimmed'>
                                    {t('editors.partner.noInviteToken')}
                                </Text>
                            )}
                        </Stack>
                    )}

                    {isVenue && !qrLoading && promoQrDataUrl && (
                        <Stack gap='xs'>
                            <Text size='sm' fw={600}>
                                {t('editors.partner.promoQr')}
                            </Text>
                            <Text size='xs' c='dimmed'>
                                {t('editors.partner.promoQrHint')}
                            </Text>
                            <Image src={promoQrDataUrl} alt='Guest promo QR' maw={220} />
                            {promoUrl && (
                                <Anchor href={promoUrl} target='_blank' size='sm'>
                                    {promoUrl}
                                </Anchor>
                            )}
                        </Stack>
                    )}

                    <Group justify='space-between' mt='md'>
                        <Button
                            color='red'
                            variant='light'
                            loading={deleting}
                            onClick={async () => {
                                if (!partner) return;
                                if (
                                    !window.confirm(
                                        t('editors.partner.deleteConfirm', { id: partner.id }),
                                    )
                                ) {
                                    return;
                                }

                                setDeleting(true);

                                try {
                                    await api.partnersPortalAdminApi.removePartner(partner.id);
                                    notifications.show({
                                        color: 'green',
                                        message: t('editors.partner.deleted'),
                                    });
                                    onDeleted?.();
                                    onSaved();
                                    onClose();
                                } catch (error) {
                                    notifications.show({
                                        color: 'red',
                                        message: apiErrorMessage(error),
                                    });
                                } finally {
                                    setDeleting(false);
                                }
                            }}
                        >
                            {t('common.delete')}
                        </Button>
                        <Group>
                            <Button variant='default' onClick={onClose}>
                                {t('common.cancel')}
                            </Button>
                            <Button loading={saving} onClick={save}>
                                {t('common.save')}
                            </Button>
                        </Group>
                    </Group>
                </Stack>
            )}
        </Drawer>
    );
};
